import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { hasMinRole, useAuth } from "@/contexts/AuthContext";
import {
  ChartLineUp, Clock, FilePdf, Plus, CheckCircle, XCircle, Download,
} from "@phosphor-icons/react";

const HEALTH_TONE = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  red:   "bg-red-100   text-red-800   border-red-300",
};

const KPI_COLOR = (v, good = 0.95) => {
  if (v == null) return "text-slate-400";
  if (v >= good) return "text-emerald-700";
  if (v >= good * 0.8) return "text-amber-700";
  return "text-red-700";
};

const buildMprUrl = (projectId, period_month) =>
  `${API}/projects/${projectId}/mpr/pdf?period_month=${period_month}`;

export default function ProjectKpiCenter({ project, trigger }) {
  const { user } = useAuth();
  const canEdit = hasMinRole(user, "manager");
  const canApprove = hasMinRole(user, "manager");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("kpi");
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadKpi = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/kpi`);
      setKpi(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && tab === "kpi") loadKpi(); /* eslint-disable-next-line */ }, [open, tab]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`kpi-${project.id}`}>
            <ChartLineUp size={12} className="mr-1" /> KPI · MPR · Time
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="kpi-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ChartLineUp size={18} className="text-emerald-600" />
            KPI · MPR · Time-log · {project.name}
          </DialogTitle>
          <DialogDescription>
            Live SPI · CPI · Milestone adherence · Utilisation · F-PM-08 MPR · F-PM-05 Time Logs
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {[
            ["kpi", "KPIs & Health", ChartLineUp],
            ["mpr", "Monthly Progress Report", FilePdf],
            ["time", "Time Logs", Clock],
          ].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition ${tab === id ? "border-[#1E5CBA] text-[#1E5CBA]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`kpi-tab-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "kpi" && <KpiPanel kpi={kpi} loading={loading} onReload={loadKpi} />}
        {tab === "mpr" && <MprPanel project={project} canEdit={canEdit} />}
        {tab === "time" && <TimePanel project={project} canEdit={canEdit} canApprove={canApprove} />}
      </DialogContent>
    </Dialog>
  );
}

const Loading = () => <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;
const Empty = ({ text }) => <div className="dc-card p-10 text-center text-sm text-slate-500">{text}</div>;

/* ─── KPI PANEL ──────────────────────────────────────────────────────── */
function KpiPanel({ kpi, loading, onReload }) {
  if (loading || !kpi) return <Loading />;
  const k = kpi.kpi;
  const fin = kpi.finance;
  const ow = kpi.open_work;

  return (
    <div className="space-y-4 pt-4">
      {/* Health hero */}
      <div className={`rounded-2xl border-2 p-5 flex items-center gap-6 ${HEALTH_TONE[kpi.health_band]}`} data-testid="health-hero">
        <div className="text-center">
          <div className="font-display text-6xl font-bold tabular-nums">{kpi.health_score}</div>
          <div className="text-xs font-mono-alt uppercase tracking-widest mt-1">/100 health</div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="text-xs font-mono-alt uppercase tracking-widest opacity-70">Current phase</div>
          <div className="font-display text-2xl font-semibold">{kpi.current_phase || "P1"}</div>
          <div className="text-xs">Progress · <strong>{kpi.progress_pct.toFixed(1)}%</strong> · Period {kpi.period_start} → {kpi.period_end}</div>
        </div>
        <Button variant="outline" size="sm" onClick={onReload}>Refresh</Button>
      </div>

      {/* 6 KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiTile label="SPI (Schedule)" value={k.spi} fmt="ratio" tone={KPI_COLOR(k.spi, 0.95)} hint="Earned / Planned ≥ 0.95 target" />
        <KpiTile label="CPI (Cost)" value={k.cpi} fmt="ratio" tone={KPI_COLOR(k.cpi, 0.95)} hint="EV / AC ≥ 0.95 target" />
        <KpiTile label="Milestone adherence" value={k.milestone_adherence_pct} fmt="pct" tone={KPI_COLOR(k.milestone_adherence_pct / 100, 0.85)} hint="On-time vs total milestones" />
        <KpiTile label="On-time submission" value={k.on_time_submission_pct} fmt="pct" hint="F-PM-10 → client" />
        <KpiTile label="Utilisation" value={k.utilisation_pct} fmt="pct" hint="Logged / available" />
        <KpiTile label="Billable hours ratio" value={k.billable_pct} fmt="pct" hint="Billable / total" />
      </div>

      {/* Finance */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Budget" value={fin.budget_total} fmt="usd" tone="text-slate-900" />
        <KpiTile label="Earned value" value={fin.earned_value} fmt="usd" />
        <KpiTile label="Actual cost" value={fin.actual_cost} fmt="usd" />
        <KpiTile label="Variance" value={fin.variance} fmt="usd" tone={fin.variance >= 0 ? "text-emerald-700" : "text-red-700"} />
      </div>

      {/* Open work */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="overline mb-3">Open work posture</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
          <Counter label="Risks open" value={ow.risks} sub={`${ow.risks_critical} critical`} tone={ow.risks_critical > 0 ? "red" : "slate"} />
          <Counter label="Issues open" value={ow.issues} tone={ow.issues > 3 ? "amber" : "slate"} />
          <Counter label="CRs pending" value={ow.change_requests_pending} tone="slate" />
          <Counter label="NCRs open" value={ow.ncrs_open} sub={`${ow.ncrs_critical} critical`} tone={ow.ncrs_critical > 0 ? "red" : "slate"} />
          <Counter label="Hours this period" value={kpi.time.total_hours} sub={`${kpi.time.contributors} contributors`} tone="slate" />
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, fmt, tone = "text-slate-900", hint }) {
  let display = "—";
  if (value !== null && value !== undefined && !isNaN(value)) {
    if (fmt === "ratio") display = Number(value).toFixed(2);
    else if (fmt === "pct") display = `${Number(value).toFixed(1)}%`;
    else if (fmt === "usd") display = formatCurrency(value);
    else display = value;
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${tone}`}>{display}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function Counter({ label, value, sub, tone = "slate" }) {
  const palette = {
    slate: "text-slate-900", amber: "text-amber-700", red: "text-red-700",
    emerald: "text-emerald-700",
  };
  return (
    <div>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-xl font-semibold tabular-nums ${palette[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

/* ─── MPR PANEL ──────────────────────────────────────────────────────── */
function MprPanel({ project, canEdit }) {
  const today = new Date();
  const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(ym);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/mpr/snapshots`);
      setSnapshots(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const snapshot = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/mpr/snapshot?period_month=${period}`);
      toast.success(`MPR snapshot saved for ${period}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const downloadPdf = async (pm) => {
    try {
      const token = getToken();
      const res = await fetch(buildMprUrl(project.id, pm), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MPR_${project.name}_${pm}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Failed to download MPR"); }
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Reporting month *</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" data-testid="mpr-period" />
        </div>
        <Button onClick={() => downloadPdf(period)} className="btn-primary-blue" data-testid="mpr-download">
          <Download size={11} className="mr-1" /> Download MPR PDF
        </Button>
        {canEdit && (
          <Button variant="outline" onClick={snapshot} disabled={saving} data-testid="mpr-snapshot">
            {saving ? "Saving…" : "Save snapshot"}
          </Button>
        )}
        <div className="ml-auto text-[11px] text-blue-700 max-w-xs">
          PDF auto-includes phase status, KPIs, finance, risks/issues/CRs/NCRs and time logged. Generated by {user?.email || "you"}.
        </div>
      </div>

      <div>
        <h4 className="font-display text-sm font-semibold mb-2">Snapshot history</h4>
        {loading ? <Loading /> : snapshots.length === 0 ? <Empty text="No snapshots taken yet. Save a snapshot to lock this month's KPIs for trend tracking." /> : (
          <div className="dc-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr><th className="px-3 py-2">Period</th><th className="px-3 py-2">SPI</th><th className="px-3 py-2">CPI</th><th className="px-3 py-2">Health</th><th className="px-3 py-2">Hours</th><th className="px-3 py-2">Risks/NCRs</th><th className="px-3 py-2">Saved</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {snapshots.map((s) => {
                  const k = s.data?.kpi || {};
                  const ow = s.data?.open_work || {};
                  return (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono-alt text-xs font-semibold">{s.period_month}</td>
                      <td className="px-3 py-2 font-mono-alt text-xs">{k.spi != null ? k.spi.toFixed(2) : "—"}</td>
                      <td className="px-3 py-2 font-mono-alt text-xs">{k.cpi != null ? k.cpi.toFixed(2) : "—"}</td>
                      <td className="px-3 py-2"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${HEALTH_TONE[s.data?.health_band]}`}>{s.data?.health_score} · {s.data?.health_band}</span></td>
                      <td className="px-3 py-2 font-mono-alt text-xs">{s.data?.time?.total_hours?.toFixed(1) || "0"}h</td>
                      <td className="px-3 py-2 text-xs">{ow.risks || 0}R · {ow.ncrs_open || 0}NCR</td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(s.period_month)} data-testid={`mpr-pdf-${s.period_month}`}>
                          <FilePdf size={11} className="mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── TIME LOG PANEL ────────────────────────────────────────────────── */
function TimePanel({ project, canEdit, canApprove }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_hours: 0, billable_hours: 0, billable_pct: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ project_id: project.id });
      if (filter !== "all") params.append("status", filter);
      const { data } = await api.get(`/time-logs?${params}`);
      setItems(data.items || []);
      setStats({ total: data.total, total_hours: data.total_hours, billable_hours: data.billable_hours, billable_pct: data.billable_pct });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    try {
      await api.post(`/time-logs/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/time-logs/${id}/reject?comment=Rejected via UI`);
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkApprovePending = async () => {
    const ids = items.filter((i) => i.status === "pending").map((i) => i.id);
    if (!ids.length) { toast.info("No pending entries"); return; }
    try {
      const { data } = await api.post("/time-logs/bulk-approve", { ids });
      toast.success(`Approved ${data.approved} entries`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-4 gap-3">
        <KpiTile label="Total entries" value={stats.total} />
        <KpiTile label="Total hours" value={stats.total_hours} />
        <KpiTile label="Billable hours" value={stats.billable_hours} />
        <KpiTile label="Billable %" value={stats.billable_pct} fmt="pct" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["all", "pending", "approved", "rejected"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`rounded-full px-3 py-1 text-xs font-medium ${filter === f ? "bg-[#0f1e42] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`time-filter-${f}`}>
            {f}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {canApprove && <Button variant="outline" size="sm" onClick={bulkApprovePending} data-testid="time-bulk-approve"><CheckCircle size={11} className="mr-1" /> Approve all pending</Button>}
          {canEdit && <Button size="sm" className="btn-primary-blue" onClick={() => setShowForm(true)} data-testid="time-create-btn"><Plus size={11} className="mr-1" /> Log time</Button>}
        </div>
      </div>

      {loading ? <Loading /> : items.length === 0 ? <Empty text="No time entries yet for this project." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Staff</th><th className="px-3 py-2">Hours</th><th className="px-3 py-2">Billable</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50" data-testid={`time-row-${t.id}`}>
                  <td className="px-3 py-2 font-mono-alt text-xs">{t.date}</td>
                  <td className="px-3 py-2 text-xs">{t.staff_name || t.staff_email}</td>
                  <td className="px-3 py-2 font-mono-alt tabular-nums">{t.hours.toFixed(2)}h</td>
                  <td className="px-3 py-2">{t.billable ? <span className="inline-block rounded px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 font-mono-alt">B</span> : <span className="text-slate-400 text-[10px]">non-billable</span>}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 line-clamp-1">{t.description}</td>
                  <td className="px-3 py-2"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${t.status === "approved" ? "bg-emerald-100 text-emerald-700" : t.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{t.status}</span></td>
                  <td className="px-3 py-2 text-right">
                    {canApprove && t.status === "pending" && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" className="h-7" onClick={() => approve(t.id)} data-testid={`time-approve-${t.id}`}><CheckCircle size={10} /></Button>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => reject(t.id)} data-testid={`time-reject-${t.id}`}><XCircle size={10} /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <TimeLogForm project={project} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function TimeLogForm({ project, onClose, onSaved }) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    staff_email: user?.email || "",
    staff_name: user?.name || "",
    date: today, hours: 1, billable: true, description: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/time-logs", { ...f, project_id: project.id, hours: Number(f.hours) });
      toast.success("Time logged");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log time</DialogTitle>
          <DialogDescription>F-PM-05 · 0.25 hour increments</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label>Staff email *</Label><Input value={f.staff_email} onChange={(e) => setF({ ...f, staff_email: e.target.value })} data-testid="time-email" /></div>
          <div><Label>Date *</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          <div><Label>Hours *</Label><Input type="number" step="0.25" min="0.25" max="24" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="time-hours" /></div>
          <div className="col-span-2 flex items-center justify-between rounded border p-2">
            <Label>Billable to client</Label>
            <Switch checked={f.billable} onCheckedChange={(v) => setF({ ...f, billable: v })} data-testid="time-billable" />
          </div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What was worked on?" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.staff_email || !f.hours} className="btn-primary-blue" onClick={submit} data-testid="time-save">{busy ? "Saving…" : "Log time"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
